import moment from 'moment';
import merge from 'deepmerge';

import Config from 'config';
import * as alerts from 'actions/alertActions';

import * as formatter from './formatter';
import * as formValidation from './formValidation';
import * as billingHelpers from './billingHelpers';
import {Sorting, ItemTypes, urls} from './constants';

export {billingHelpers, formatter, formValidation};

export const deepCopy = obj => merge(obj, {});

export const isIOS =
  !!navigator.platform && /iPad|iPhone|iPod/.test(navigator.platform);

const errorTrace = error => {
  console.error('Error :', error); // eslint-disable-line no-console

  return error;
};

export const setPopupTitle = title => {
  const titleBar = parent.document.querySelector('.rwTitlebarControls');

  if (titleBar) {
    titleBar.querySelector('em').innerText = title;
  }
};

export const hideActiveToolTip = () => {
  if (typeof window.MHD.UI.RadTooltip.hideActiveToolTip === 'function') {
    window.MHD.UI.RadTooltip.hideActiveToolTip();
  }
};

export const handleError = (message, error, dispatch = null) => {
  if (error) errorTrace(error);
  const errorMsg = error && error.message ? `: ${error.message}` : error || '';
  const text = `${message} ${errorMsg}`;
  if (typeof dispatch === 'function') {
    dispatch(alerts.error(text));
  } else {
    toasterError(text);
  }
};

export const trace = label => value => {
  console.log(`${label} :`, value); // eslint-disable-line no-console

  return value;
};

export const validEmail = emailAddress => {
  const emailRegex = /([a-zA-Z0-9_\.\-'])+\@(([a-zA-Z0-9\-])+\.)+([\w-]{2,24})+/g;

  return emailRegex.test(emailAddress);
};

export const getDateFormat = portalSettings => {
  const dateFormatSetting = portalSettings.DateFormat;

  return dateFormatSetting.value
    ? dateFormatSetting.value
    : dateFormatSetting.defaultValue;
};

export const getDateTimeFormat = portalSettings => {
  const dateTimeFormatSetting = portalSettings.DateTimeFormat;

  return dateTimeFormatSetting.value
    ? dateTimeFormatSetting.value
    : dateTimeFormatSetting.defaultValue;
};

export const momentToTicks = time =>
  time.valueOf() * 10000 + 621355968000000000;

export const toLocal = dt =>
  moment(`${dt.format('YYYY-MM-DD HH:mm')} +0000`, 'YYYY-MM-DD HH:mm Z');

export const reverseStringBoolean = value => !(value === 'true').toString();

export function parseNumber(str) {
  return Number.isNaN(str) ? null : Number(str);
}

const QBDesktop = 'QBDesktop';
const QBOnline = 'QBOnline';

export const toggleQuickBookSettings = (settings, newSetting) => {
  const qbDesktop = settings.find(s => s.name.indexOf(QBDesktop) > -1);
  const qbOnline = settings.find(s => s.name.indexOf(QBOnline) > -1);

  if (newSetting.name.indexOf('Desktop') > -1 && qbOnline.value === 'true') {
    qbOnline.value = reverseStringBoolean(newSetting.value);
  }

  if (newSetting.name.indexOf('Online') > -1 && qbDesktop.value === 'true') {
    qbDesktop.value = reverseStringBoolean(newSetting.value);
    window.MHD.UI.BootstrapMenu.hideQBIcon();
  }
};

export const commonSort = ({
  sortOrder,
  sortColumn,
  secondaryColumn = null,
  secondaryOrder = Sorting.asc,
  dateSortColumns = [],
}) => (a, b) => {
  if (
    !Object.prototype.hasOwnProperty.call(a, sortColumn) ||
    !Object.prototype.hasOwnProperty.call(b, sortColumn)
  )
    return 0; // property doesn't exist

  const valueA =
    typeof a[sortColumn] === 'string'
      ? a[sortColumn].toLowerCase()
      : a[sortColumn];
  const valueB =
    typeof b[sortColumn] === 'string'
      ? b[sortColumn].toLowerCase()
      : b[sortColumn];

  let comparison = 0;

  if (typeof valueA === 'number' || typeof valueB === 'number') {
    if (valueA === null) {
      comparison = -1;
    } else if (valueB === null) {
      comparison = 1;
    } else {
      comparison = valueA < valueB ? -1 : valueA > valueB ? 1 : 0;
    }
  } else {
    const letterA = valueA ? valueA.charAt(0).match(/[a-z0-9]/i) : null;
    const letterB = valueB ? valueB.charAt(0).match(/[a-z0-9]/i) : null;

    if (dateSortColumns.length > 0 && dateSortColumns.includes(sortColumn)) {
      comparison = new Date(valueA) - new Date(valueB);
    } else if (letterA && !letterB) {
      comparison = 1;
    } else if (!letterA && letterB) {
      comparison = -1;
    } else if (valueA > valueB) {
      comparison = 1;
    } else if (valueA < valueB) {
      comparison = -1;
    }
  }

  if (comparison === 0 && secondaryColumn !== null) {
    if (a[secondaryColumn] < b[secondaryColumn]) {
      return secondaryOrder === Sorting.asc ? -1 : 1;
    } else if (a[secondaryColumn] > b[secondaryColumn]) {
      return secondaryOrder === Sorting.asc ? 1 : -1;
    }
  }

  return sortOrder === Sorting.desc ? comparison * -1 : comparison;
};

export function reloadPortalSettings() {
  // This is a temporary work around that will clear session values w/o destroying the user session.
  const sessionClearUrl = 'ReloadPortalSetting.aspx';
  const completeUrl = `${
    window.MHD.Global.Configuration.BaseUrl
  }/${sessionClearUrl}`;

  window.$.get(completeUrl);
}

export function reloadSessionWithPortalName(portalName) {
  const sessionClearUrl = 'ReloadSession.aspx';
  const completeUrl = `${
    window.MHD.Global.Configuration.BaseUrl
  }/${sessionClearUrl}`;

  window.$.get(completeUrl).then(() => {
    document.getElementById('spanPortalName').innerText = portalName;
  });
}

export function urlForPriceListItem(itemId, itemType, source) {
  return `/Modules/ServiceSettings/ServiceAddEditFull.aspx?ItemId=${itemId}&ItemType=${itemType}&Source=${source}`;
}

export function gotoPriceListItem(itemId, itemType, source) {
  window.location.href = urlForPriceListItem(itemId, itemType, source);
}

export const toFixed = (num, decimalPlaces) => {
  var d = decimalPlaces || 0;
  var m = Math.pow(10, d);
  var n = +(d ? num * m : num).toFixed(8); // Avoid rounding errors
  var i = Math.floor(n),
    f = n - i;
  var e = 1e-8; // Allow for rounding errors in f
  var r = f > 0.5 - e && f < 0.5 + e ? (i % 2 == 0 ? i : i + 1) : Math.round(n);
  return Number(d ? r / m : r).toFixed(decimalPlaces);
};

export const unSAFE__toFixed = (number, decimals) =>
  Number(`${Math.round(`${number}e${decimals}`)}e-${decimals}`).toFixed(
    decimals,
  );

export function isPercentage(itemTypeId) {
  const itemTypes = [
    ItemTypes.DiscountByPercentage,
    ItemTypes.OtherChargeByPercentage,
    ItemTypes.Tax,
  ];

  return itemTypes.includes(itemTypeId);
}

export const isOtherCharge = itemTypeId =>
  itemTypeId === ItemTypes.OtherCharge ||
  itemTypeId === ItemTypes.OtherChargeByPercentage;

export const isDiscount = itemTypeId =>
  itemTypeId === ItemTypes.Discount ||
  itemTypeId === ItemTypes.DiscountByPercentage;

export function nextId(list, id, idField, searchForwards) {
  const idx = list.findIndex(x => x[idField] === id);
  if (!searchForwards) {
    return idx > 0 ? list[idx - 1][idField] : null;
  }
  return idx < list.length - 1 ? list[idx + 1][idField] : null;
}

export const goToBilling = (
  isUnpaid,
  navigateTo,
  fusebillEnabled,
  fuseBillUrl = null,
) => {
  if (!fusebillEnabled) {
    window.location.href = '/Modules/MyAccount/MyPlan.aspx';
    return;
  }

  if (isUnpaid || !fuseBillUrl) {
    navigateTo('plancheckout.planselection');
  } else {
    window.location.href = fuseBillUrl;
  }
};

export const isUnpaid = (portalInfo, subscription) => {
  if (!portalInfo || (portalInfo && portalInfo.isDemo)) return true;
  return (
    !!portalInfo.hA_ServiceProfessionalId &&
    (!subscription ||
      (!subscription.billingCustomerId && !subscription.subscriptionID))
  );
};

export const displayTerm = term => {
  switch (term) {
    case 'Monthly':
      return 'Month';

    case 'Quarterly':
      return 'Quarter';

    case 'Yearly':
      return 'Year';

    default:
      return '';
  }
};

export const objectToParams = obj =>
  Object.keys(obj)
    .filter(key => obj[key] !== undefined)
    .map(
      key => `${key}=${obj[key] !== null ? encodeURIComponent(obj[key]) : ''}`,
    )
    .join('&');

export const htmlDecode = input => {
  const doc = new DOMParser().parseFromString(input, 'text/html');
  return doc.documentElement.textContent;
};

export const capitalize = str =>
  `${str.charAt(0).toUpperCase()}${str.slice(1)}`;

export const flattenSortOrder = (order, shorten = true) => {
  if (shorten) {
    if (order === 'ascending') {
      return 'asc';
    }

    if (order === 'descending') {
      return 'desc';
    }

    return order;
  }

  if (order === 'asc') {
    return 'ascending';
  }

  if (order === 'desc') {
    return 'descending';
  }

  return order;
};

export const buildTypesTree = (types, currentType, depth = '') => {
  const currentChildren = types
    .filter(type => type.parentId === currentType.equipmentTypeId)
    .sort(commonSort({sortOrder: Sorting.desc, sortColumn: 'category'}));

  if (currentChildren.length > 0) {
    let subChildren = [];

    currentChildren.forEach(child => {
      const buildResults = buildTypesTree(types, child, `${depth}...`);

      subChildren =
        subChildren.length > 0
          ? subChildren.concat(buildResults)
          : buildResults;
    });

    subChildren.unshift({
      ...currentType,
      value: currentType.equipmentTypeId,
      text: `${depth}${currentType.category}`,
    });

    return subChildren;
  } else {
    return [
      {
        ...currentType,
        value: currentType.equipmentTypeId,
        text: `${depth}${currentType.category}`,
      },
    ];
  }
};

export const rowIndexFromPage = pageInfo =>
  (pageInfo.pageNumber - 1) * pageInfo.pageSize;

export const sortParam = sortInfo =>
  sortInfo.sortColumn
    ? `${sortInfo.sortOrder === Sorting.desc ? '-' : ''}${sortInfo.sortColumn}`
    : '';

export const isTouchDevice = () => {
  // https://stackoverflow.com/a/4819886
  const prefixes = ' -webkit- -moz- -o- -ms- '.split(' ');
  const mq = query => window.matchMedia(query).matches;

  if (
    'ontouchstart' in window ||
    (window.DocumentTouch && document instanceof DocumentTouch)
  ) {
    return true;
  }

  // include the 'heartz' as a way to have a non matching MQ to help terminate the join
  // https://git.io/vznFH
  const query = ['(', prefixes.join('touch-enabled),('), 'heartz', ')'].join(
    '',
  );

  return mq(query);
};

export const isPaymentsApp = () => !!document.getElementById('paymentRoot');

export const showPopUp = (portalId, params, reactInvoiceEstimateEdit) => {
  let url;
  let reactPage = false;
  const paramObj = {};
  const ticketId = params.ticketId || params.TicketId || params.ticketid;
  const estimateId =
    params.estimateId || params.EstimateId || params.estimateid;
  const invoiceId = params.invoiceId || params.InvoiceId || params.invoiceid;

  if (invoiceId && ticketId) {
    if (reactInvoiceEstimateEdit) {
      reactPage = true;
      url = `/ticket/${ticketId}/invoice?invoiceId=${invoiceId}`;
    } else {
      url = urls.InvoiceEditPopup;
      paramObj.InvoiceId = invoiceId;
      paramObj.ticketId = ticketId;
      paramObj.tabId = portalId;
    }
  } else if (estimateId && ticketId) {
    if (reactInvoiceEstimateEdit) {
      reactPage = true;
      url = `/ticket/${ticketId}/estimate?estimateId=${estimateId}`;
    } else {
      url = urls.EstimatePopup;
      paramObj.woid = estimateId;
      paramObj.ticketId = ticketId;
    }
  } else if (ticketId) {
    url = urls.TicketEdit;
    paramObj.ticketId = ticketId;
    paramObj.tabId = portalId;
  }

  if (url) {
    const radUrl = reactPage
      ? `${urls.RmodulesPopup}${url}`
      : `${url}?${objectToParams(paramObj)}`;

    setTimeout(() => window.radopen(radUrl), 300); //doesn't work without the delay
  }
};

export const arrayToObject = (arr, field) => {
  if (!arr) return {};
  const obj = {};
  arr.forEach(item => {
    obj[item[field]] = item;
  });
  return obj;
};

export const openPrintWindow = url => {
  const width = window.screen.width;
  const height = window.screen.height - 60;
  const params = [
    'toolbar=no',
    'status=no',
    'menubar=no',
    'scrollbars=yes',
    'resizable=yes',
    `top=${window.screenY}`,
    `left=${window.screenX}`,
    `width=${width}`,
    `height=${height}`,
    'fullscreen=yes',
  ].join(',');

  window.open(url, 'PrintVersion', params);
};

export const closePopover = () => {
  if (typeof window.MHD.UI.RadWindow.getRadWindow == 'function') {
    const oWin = window.MHD.UI.RadWindow.getRadWindow();

    if (oWin) {
      oWin.close();
    }
  }
};

export const makeCurrentWinDirty = () => {
  if (
    window.MHD &&
    window.MHD.UI &&
    window.MHD.UI.RadWindow &&
    typeof window.MHD.UI.RadWindow.makeCurrentWinDirty === 'function'
  ) {
    window.MHD.UI.RadWindow.makeCurrentWinDirty();
  }
};

export const getContactName = (contact, contactDictionaryTerm) =>
  `${
    contact.name // has full name
      ? `${contact.name}`
      : contact.firstname && contact.lastname // has both first and last name
        ? `${contact.firstname} ${contact.lastname}`
        : contact.firstname // has only first name
          ? `${contact.firstname}`
          : contact.lastname // has only last name
            ? `${contact.lastname}`
            : `No ${contactDictionaryTerm} name.` // doesn't have first or last name
  }`;

export const hasValue = x => x !== undefined && x !== null;

export const removeAutoComplete = () =>
  document
    .querySelectorAll('div.ui.dropdown input')
    .forEach(d => d.setAttribute('autocomplete', 'disabled'));

export const toasterWarning = message => window.toastr.warning(message);
export const toasterError = message => window.toastr.error(message);
export const toasterSuccess = message => window.toastr.success(message);

export const sortByColumn = (sortColumn, sortInfo) => ({
  sortColumn,
  sortOrder:
    !sortInfo ||
    sortInfo.sortColumn !== sortColumn ||
    sortInfo.sortOrder === Sorting.desc
      ? Sorting.asc
      : Sorting.desc,
});

export const getPage = (items, pageInfo) => {
  const {pageSize, pageNumber} = pageInfo;
  const start = (pageNumber - 1) * pageSize;
  const end = start + pageSize;
  return items.slice(start, end >= items.count ? items.count - 1 : end);
};

export const hasLowInventory = item =>
  item &&
  item.reOrderPoint !== null &&
  item.quantityOnHand <= item.reOrderPoint;



// WEBPACK FOOTER //
// ./src/helpers/index.js